import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, ExternalLink } from "lucide-react";

export const ConsoleDocumenti = ({ client, tenants }) => {
  const [docs, setDocs] = useState([]);
  const [voce, setVoce] = useState(null);
  const [slug, setSlug] = useState("");

  useEffect(() => {
    client
      .get("/admin/docs")
      .then((r) => setDocs(r.data.docs))
      .catch(() => toast.error("Documenti non disponibili"));
    client
      .get("/admin/voice-usage")
      .then((r) => setVoce(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ottieni = async (d) => {
    const url = (d.per_locale && slug ? `${d.url}?tenant=${slug}` : d.url).replace("/api", "");
    const r = await client.get(url, { responseType: "blob" });
    return URL.createObjectURL(r.data);
  };

  const scarica = async (d) => {
    try {
      const href = await ottieni(d);
      const a = document.createElement("a");
      a.href = href;
      a.download = d.per_locale && slug ? `${slug}-${d.file}` : d.file;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      toast.error("Scaricamento non riuscito");
    }
  };

  const apri = async (d) => {
    try {
      window.open(await ottieni(d), "_blank");
    } catch {
      toast.error("Apertura non riuscita");
    }
  };

  return (
    <Card className="space-y-4 border-slate-800 bg-[#111827] p-5 text-slate-100" data-testid="console-docs">
      <div>
        <h2 className="font-head flex items-center gap-2 text-lg font-bold">
          <FileText className="h-4 w-4 text-amber-400" /> Documenti da consegnare
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          PDF sempre generati aggiornati. L'informativa privacy può uscire intestata al locale: scegli
          l'attività qui sotto.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Intestata a</span>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          data-testid="docs-tenant-select"
          className="h-10 rounded-md border border-slate-800 bg-[#0b1220] px-3 text-sm text-slate-200"
        >
          <option value="">Nessun locale (versione in bianco)</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.slug}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {docs.map((d) => (
          <div
            key={d.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 p-3"
            data-testid={`doc-${d.id}`}
          >
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {d.name}
                {d.version && (
                  <span className="font-num rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
                    v{d.version} · {d.date}
                  </span>
                )}
                {d.per_locale && slug && (
                  <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                    intestata
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-[11px] text-slate-400">{d.desc}</p>
            </div>
            <Button size="sm" onClick={() => scarica(d)} data-testid={`doc-download-${d.id}`}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Scarica
            </Button>
            <button
              onClick={() => apri(d)}
              className="flex items-center gap-1 text-[11px] font-semibold text-sky-400 hover:underline"
              data-testid={`doc-open-${d.id}`}
            >
              Apri <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <p className="rounded-lg border border-slate-800 bg-[#0b1220] p-3 text-[11px] text-slate-400" data-testid="docs-update-note">
        Per aggiornare l'informativa basta dire cosa cambiare: il testo sta in un unico file
        (<span className="font-num">backend/privacy_doc.py</span>) e il PDF si rigenera al download
        successivo, con versione e data nuove in prima pagina.
      </p>

      <div className="space-y-2 rounded-lg border border-slate-800 p-3" data-testid="docs-voice-usage">
        <p className="text-sm font-semibold">Voce degli annunci</p>
        <p className="font-num text-[11px] text-slate-400">
          Voce italiana del dispositivo, nessun costo a consumo. Voce registrata dal server come
          riserva{voce?.cache ? ` · ${voce.cache} frasi già in memoria` : ""}.
        </p>
      </div>

      <div className="space-y-2 rounded-lg border border-slate-800 p-3" data-testid="docs-demo-links">
        <p className="text-sm font-semibold">Accesso diretto alla demo</p>
        <p className="text-[11px] text-slate-400">
          Un tocco e sei dentro l'Osteria del Vicolo, senza codice attività né PIN. Salvali tra i
          preferiti del telefono.
        </p>
        {[
          ["Titolare", "/demo"],
          ["Sala (cameriere)", "/demo/sala"],
          ["Monitor cucina", "/demo/cucina"],
          ["Direttore", "/demo/direttore"],
        ].map(([label, path]) => (
          <div key={path} className="flex flex-wrap items-center gap-2" data-testid={`demo-link-${path.replace(/\//g, "-")}`}>
            <span className="w-40 shrink-0 text-xs text-slate-300">{label}</span>
            <span className="font-num min-w-0 flex-1 truncate text-[11px] text-slate-500">
              {window.location.origin}
              {path}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}${path}`);
                toast.success("Link copiato");
              }}
            >
              Copia
            </Button>
            <a href={path} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-sky-400 hover:underline">
              Apri
            </a>
          </div>
        ))}
      </div>
    </Card>
  );
};
